import React, { useEffect, useState } from "react";
import {
  Container, Card, CardBody, Table, Button, Spinner, Badge, Row, Col,
  Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup, Alert,
} from "reactstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  getPortalById, getTenantsByPortal, createTenant, deleteTenant,
  getPropertiesByOwner, createProperty, getPropertyById,
  getTenantByEmailAny, getDeletedTenantsByPortal, restoreTenant,
} from "../../../helpers/firestoreService";
import { useAuthUser } from "../../../hooks/AuthProvider";
import homeBg from "../../../assets/images/home.png";

const TenantList = () => {
  document.title = "Tenants | Support Portal";

  const { portalId } = useParams();
  const navigate = useNavigate();
  const user = useAuthUser();
  const [portal, setPortal] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoring, setRestoring] = useState(null);

  // New tenant form
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPropertyId, setNewPropertyId] = useState("");

  // Properties
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);

  // Property address cache for table display
  const [propertyCache, setPropertyCache] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const p = await getPortalById(portalId);
      if (!p) {
        navigate("/portals");
        return;
      }
      setPortal(p);
      const tenantList = showDeleted
        ? await getDeletedTenantsByPortal(portalId)
        : await getTenantsByPortal(portalId);
      setTenants(tenantList);

      // Resolve property addresses for display
      const propertyIds = [...new Set(tenantList.map((t) => t.propertyId).filter(Boolean))];
      const cache = { ...propertyCache };
      await Promise.all(
        propertyIds
          .filter((id) => !cache[id])
          .map((id) =>
            getPropertyById(id).then((prop) => {
              if (prop) cache[id] = prop;
            })
          )
      );
      setPropertyCache(cache);
    } catch (e) {
      console.error("Failed to load tenants:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    if (!user) return;
    setPropertiesLoading(true);
    try {
      setProperties(await getPropertiesByOwner(user.uid));
    } catch (e) {
      console.error("Load properties error:", e);
    } finally {
      setPropertiesLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [portalId, showDeleted]);

  useEffect(() => {
    loadProperties();
  }, [user]);

  const handleCreate = async () => {
    setError("");
    if (!newName.trim() || !newEmail.trim()) {
      setError("Name and email are required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    setSaving(true);
    try {
      // Duplicate email guard
      const existing = await getTenantByEmailAny(newEmail.trim());
      if (existing) {
        if (existing.deleted) {
          setCreateModal(false);
          navigate(`/tenants/${portalId}/${existing.id}`);
          return;
        }
        setError("A tenant with this email already exists.");
        setSaving(false);
        return;
      }
      await createTenant({
        name: newName.trim(),
        email: newEmail.trim(),
        propertyId: newPropertyId || "",
      });
      setNewName("");
      setNewEmail("");
      setNewPropertyId("");
      setCreateModal(false);
      await load();
    } catch (e) {
      console.error("Create tenant failed:", e);
      setError("Failed to create tenant. Please try again.");
    } finally {
      setSaving(false);
    }
  };



  const handleRestore = async (tenantId) => {
    setRestoring(tenantId);
    try {
      await restoreTenant(tenantId);
      await load();
    } catch (e) {
      console.error("Restore tenant failed:", e);
    } finally {
      setRestoring(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteTenant(deleteTarget.id);
      setDeleteTarget(null);
      setDeleteModal(false);
      await load();
    } catch (e) {
      console.error("Delete tenant failed:", e);
    } finally {
      setSaving(false);
    }
  };

  const getPropertyAddress = (tenant) => {
    if (tenant.propertyId && propertyCache[tenant.propertyId]) {
      return propertyCache[tenant.propertyId].address;
    }
    // Fallback for legacy tenants with address field
    return tenant.address || null;
  };

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* ── Hero Banner ──────────────────────────────────────────────────── */}
        <div className="rounded mb-4 p-4 text-white position-relative overflow-hidden" style={{ minHeight: 180 }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${homeBg})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.18 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)" }} />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <Row className="align-items-center">
              <Col>
                <h3 className="mb-1 text-white">Tenants</h3>
                {portal && (
                  <p className="mb-0 opacity-75">
                    Portal: {portal.name}
                  </p>
                )}
              </Col>
              <Col xs="auto" className="d-flex gap-2">
                <Button
                  color="light"
                  outline
                  className="text-white border-white"
                  onClick={() => { setShowDeleted((v) => !v); }}
                >
                  <i className={`bx ${showDeleted ? "bx-hide" : "bx-trash"} me-1`} />
                  {showDeleted ? "Hide Deleted" : "Show Deleted"}
                </Button>
                <Button color="light" outline className="text-white border-white" onClick={() => navigate(`/portals/${portalId}`)}>
                  <i className="bx bx-arrow-back me-1" /> Back to Portal
                </Button>
                <Button color="light" onClick={() => { setError(""); setCreateModal(true); }}>
                  <i className="bx bx-plus me-1" /> Add Tenant
                </Button>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs="auto">
                <Badge color="light" className="text-dark fs-6 px-3 py-2">
                  <i className="bx bx-user me-1" />
                  {tenants.length} Tenant{tenants.length !== 1 ? "s" : ""}
                </Badge>
              </Col>
            </Row>
          </div>
        </div>

        {tenants.length === 0 ? (
          <Card>
            <CardBody className="text-center py-5">
              <i className="bx bx-user text-muted" style={{ fontSize: "2.5rem" }} />
              <h5 className="text-muted mt-2">No tenants yet</h5>
              <p className="text-muted">
                Add tenants to this portal. Tenants are users who can access this portal for support.
              </p>
              <Button color="primary" onClick={() => { setError(""); setCreateModal(true); }}>
                Add Tenant
              </Button>
            </CardBody>
          </Card>
        ) : (
          <Card>
            <CardBody>
              <div className="table-responsive">
                <Table className="table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Property</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((t) => (
                      <tr key={t.id}>
                        <td className={t.deleted ? "text-muted text-decoration-line-through" : ""}>
                          <Link to={`/tenants/${portalId}/${t.id}`}>
                            {t.name || t.email}
                          </Link>
                          {t.deleted && <Badge color="secondary" className="ms-2">Deleted</Badge>}
                        </td>
                        <td>{t.email}</td>
                        <td>
                          {t.propertyId && propertyCache[t.propertyId] ? (
                            <Link to={`/properties/${t.propertyId}`}>
                              {propertyCache[t.propertyId].address}
                            </Link>
                          ) : (
                            getPropertyAddress(t) || <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {t.createdAt ? t.createdAt.toLocaleDateString() : "—"}
                        </td>
                        <td>
                          <Link
                            to={`/tenants/${portalId}/${t.id}`}
                            className="btn btn-sm btn-outline-secondary me-2"
                          >
                            View
                          </Link>
                          {showDeleted ? (
                            <Button
                              size="sm"
                              color="success"
                              disabled={restoring === t.id}
                              onClick={() => handleRestore(t.id)}
                            >
                              {restoring === t.id ? <Spinner size="sm" /> : "Restore"}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              color="outline-danger"
                              onClick={() => {
                                setDeleteTarget(t);
                                setDeleteModal(true);
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Create Tenant Modal */}
        <Modal isOpen={createModal} toggle={() => setCreateModal(false)}>
          <ModalHeader toggle={() => setCreateModal(false)}>
            Add Tenant
          </ModalHeader>
          <ModalBody>
            {error && <Alert color="danger">{error}</Alert>}
            <FormGroup>
              <Label>Name *</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Tenant name"
              />
            </FormGroup>
            <FormGroup>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="tenant@example.com"
              />
              <small className="text-muted">This should be the email they use to log in.</small>
            </FormGroup>
            <FormGroup>
              <Label>Property</Label>
              <div className="d-flex gap-2">
                <Input
                  type="select"
                  value={newPropertyId}
                  onChange={(e) => setNewPropertyId(e.target.value)}
                  disabled={propertiesLoading}
                >
                  <option value="">— No property —</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.address}</option>
                  ))}
                </Input>
              </div>
              <small className="text-muted">Select an existing property.</small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setCreateModal(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={!newName.trim() || !newEmail.trim() || saving}
              onClick={handleCreate}
            >
              {saving ? <Spinner size="sm" /> : "Add Tenant"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Delete Confirm Modal */}
        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
          <ModalHeader toggle={() => setDeleteModal(false)}>
            Confirm Delete
          </ModalHeader>
          <ModalBody>
            Are you sure you want to remove tenant{" "}
            <strong>{deleteTarget?.name}</strong> from this portal?
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setDeleteModal(false)}>
              Cancel
            </Button>
            <Button color="danger" disabled={saving} onClick={handleDelete}>
              {saving ? <Spinner size="sm" /> : "Delete"}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default TenantList;
